// Oversight reads (architecture sprint-11 §8.2, §8.6, §9.7): the projects the caller oversees and the
// project-scoped records, people and tracker. Admins use the same endpoints for any project.
import { keepPreviousData, useMutation, useQuery, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { LocalDate } from '@shared/calendar';
import { apiRequest, downloadFile } from '@/lib/api';
import { projectRecordsApiQuery, projectRecordsExportPath, type RecordsParams } from '@/lib/records-model';
import type { RecordsPage, Tracker } from '@/types/admin';
import type { OversightProject } from '@/types/settings';

export const OVERSIGHT_KEY = ['oversight'] as const;

export function useOversightProjects(enabled = true): UseQueryResult<OversightProject[]> {
  return useQuery({
    queryKey: [...OVERSIGHT_KEY, 'projects'],
    queryFn: async () => (await apiRequest<{ items: OversightProject[] }>('/api/oversight/projects')).items,
    enabled,
  });
}

export function useOversightRecords(projectId: string | null, params: RecordsParams): UseQueryResult<RecordsPage> {
  return useQuery({
    queryKey: [...OVERSIGHT_KEY, projectId, 'records', params],
    queryFn: () => apiRequest<RecordsPage>(`/api/oversight/projects/${projectId ?? ''}/records?${projectRecordsApiQuery(params)}`),
    enabled: projectId !== null,
    placeholderData: keepPreviousData,
  });
}

export interface ProjectPerson {
  id: string;
  name: string;
  status: 'active' | 'deactivated';
}

export function useOversightPeople(projectId: string | null): UseQueryResult<ProjectPerson[]> {
  return useQuery({
    queryKey: [...OVERSIGHT_KEY, projectId, 'people'],
    queryFn: async () => (await apiRequest<{ items: ProjectPerson[] }>(`/api/oversight/projects/${projectId ?? ''}/people`)).items,
    enabled: projectId !== null,
  });
}

/** null project = the Admin all-projects tracker (/api/admin/tracker, unchanged). */
export function useScopedTracker(projectId: string | null, from: LocalDate, to: LocalDate, enabled = true): UseQueryResult<Tracker> {
  const query = new URLSearchParams({ from, to }).toString();
  return useQuery({
    queryKey: projectId === null ? ['admin', 'tracker', from, to] : [...OVERSIGHT_KEY, projectId, 'tracker', from, to],
    queryFn: () => apiRequest<Tracker>(projectId === null ? `/api/admin/tracker?${query}` : `/api/oversight/projects/${projectId}/tracker?${query}`),
    enabled,
  });
}

export function useProjectRecordsExport(): UseMutationResult<void, Error, { projectId: string; params: RecordsParams; format: 'csv' | 'xlsx' }> {
  return useMutation({
    mutationFn: ({ projectId, params, format }) =>
      downloadFile(projectRecordsExportPath(projectId, params, format), `clockit-records_${params.from}_${params.to}.${format}`),
  });
}
